import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  capitalizeEachWord,
  convertDateToDotDate,
  getProviderInitials,
} from '../../../../utils/sharedFunctions'
import DashboardCalendar from '../../Dashboard/Components/DashboardCalendar'
import { VISIT_STATUS } from '../../../../utils/statusConstants'
import {
  canProviderAccessNote,
  getProgressNoteIcon,
  shouldHidePrivateNote,
} from '../../../../utils/sharedChartFunctions'
import { CButton } from '@coreui/react-pro'
import { setPanelDetails } from '../../../../redux/sidePanel/sidePanelSlice'
import { refreshEncounter } from '../../../../redux/sidePanel/sidePanelThunk'
import { toast } from 'react-toastify'
import {
  noteAccessErrorMessage,
  privateNoteErrorMessage,
} from '../../ProgressNote/progressNoteConstants'

function populateLabels(visitSeries) {
  let tempLabels = []
  visitSeries.forEach((visit) => {
    const index = tempLabels.findIndex((v) => v.date === visit.service_date_from)

    if (index !== -1) {
      let arr = tempLabels.splice(index, 1)
      tempLabels.push({ date: arr[0].date, title: arr[0].title + '+', color: 'transparent' })
    } else {
      if (!visit?.procedure_code_primary?.code) {
        tempLabels.push({ date: visit.service_date_from, title: '---', color: 'transparent' })
      } else if (visit?.status === VISIT_STATUS.not_seen) {
        tempLabels.push({ date: visit.service_date_from, title: '---', color: 'transparent' })
      } else {
        if (visit?.service_lines?.length > 1 || visit?.procedureCodes?.length > 1) {
          tempLabels.push({
            date: visit.service_date_from,
            title: visit.procedure_code_primary.code + '+',
            color: 'transparent',
          })
        } else {
          tempLabels.push({
            date: visit.service_date_from,
            title: visit.procedure_code_primary.code,
            color: 'transparent',
          })
        }
      }
    }
  })
  return tempLabels
}

export default function PanelVisitHistoryCard() {
  const dispatch = useDispatch()

  const censusDate = useSelector((state) => state.census.activeDate)

  const provider = useSelector((state) => state.user?.provider)
  const userId = useSelector((state) => state.user?.user?._id)

  const encounterId = useSelector((state) => state.sidePanel?.panelEncounter?._id)
  const panelHistory = useSelector((state) => state.sidePanel.panelEncHistory) || []

  const [visitLabels] = useState(populateLabels(panelHistory))
  const [selectedVisitDate, setSelectedVisitDate] = useState(censusDate)
  const [visitHistoryDisplayCount, setVisitHistory] = useState(10)

  const showMore = () => {
    setVisitHistory((prev) => prev + 10)
  }

  const handleDateChange = (event) => {
    const newDate = event.date.toISOString().substring(0, 10)
    const visitOnDay = panelHistory.find((visit) => visit.service_date_from === newDate)
    if (visitOnDay) {
      onChangeVisit(visitOnDay, newDate)
    } else {
      setSelectedVisitDate(newDate)
    }
  }

  const onChangeVisit = (visit, date) => {
    if (shouldHidePrivateNote(visit?.chart, userId)) {
      toast.info(privateNoteErrorMessage)
      return
    }
    if (!canProviderAccessNote(visit, provider)) {
      toast.error(noteAccessErrorMessage)
      return
    }

    setSelectedVisitDate(new Date(date).toISOString().substring(0, 10))
    dispatch(
      setPanelDetails({
        visitId: visit._id,
        isLastEncounter: false,
      }),
    )
    dispatch(refreshEncounter({ encounterId: visit._id }))
  }

  return (
    <div id={'PanelVisitHistoryCard'} className={'display-box'}>
      <DashboardCalendar
        handleDateChange={handleDateChange}
        handleMonthChange={() => {}}
        marks={visitLabels}
        selectedDate={selectedVisitDate}
      />

      {panelHistory?.slice(0, visitHistoryDisplayCount).map((visit) => {
        const emCode = visit?.procedure_code_primary
        const provider = visit?.rendering_provider

        let initials = getProviderInitials(provider?.first_name + ' ' + provider?.last_name)
        const ssp = visit?.incident_provider
        const sspInitials = ssp?.first_name
          ? getProviderInitials(ssp?.first_name + ' ' + ssp?.last_name)
          : ''

        const noteIcon = getProgressNoteIcon(visit, { fontSize: '0.9em', isHistory: true })

        const isSkipped = visit?.status === VISIT_STATUS.not_seen

        return (
          <div
            key={'PanelVisitHistoryCard-' + visit._id}
            className={`d-flex align-items-center visit-details ${
              selectedVisitDate === visit?.service_date_from && visit._id === encounterId
                ? 'selected-visit'
                : ''
            } ${isSkipped ? 'strikethrough' : 'pointer'}`}
            onClick={() => {
              if (!isSkipped) {
                onChangeVisit(visit, visit.service_date_from)
              }
            }}
          >
            <div className={'date padding'}>{convertDateToDotDate(visit.service_date_from)}</div>

            <div className={`cpt ${isSkipped ? 'strikethrough' : ''}`}>
              {isSkipped
                ? 'N/A'
                : visit?.procedureCodes?.length > 1
                  ? emCode?.code + ' +' + (visit?.procedureCodes?.length - 1)
                  : emCode?.code || '---'}
            </div>

            <div className={'note padding'}>{noteIcon}</div>

            {ssp && (
              <div
                title={`${provider?.first_name + ' ' + provider?.last_name}${
                  sspInitials ? '/' + ssp?.first_name + ' ' + ssp?.last_name : ''
                }`}
              >
                {initials + '/'}
                <span className={'bold'}>{sspInitials}</span>
              </div>
            )}

            {!ssp && <div>{capitalizeEachWord(visit?.rendering_provider_name)}</div>}
          </div>
        )
      })}

      {visitHistoryDisplayCount < panelHistory.length && (
        <div className="show-more">
          <CButton onClick={showMore}>Show More</CButton>
        </div>
      )}
    </div>
  )
}
